import { createContext, useCallback, type ReactNode } from "react";

import { LoadingSlide } from "@components/title-slide-view";
import { type SavedView } from "@features/sales-v3/hooks/use-saved-views-queries";
import {
  CREATE_HIDDEN_SALES_SAVED_VIEW_TEMPLATE,
  CREATE_SALES_SAVED_VIEW,
  DELETE_SALES_SAVED_VIEW,
  GET_SALES_SAVED_VIEWS,
  UPDATE_SALES_SAVED_VIEW,
  UPDATE_SALES_SAVED_VIEW_ORDERING,
  UPDATE_SALES_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS,
} from "@gql/sales-saved-views";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type CreateHiddenSalesSavedViewTemplate,
  type CreateHiddenSalesSavedViewTemplateVariables,
  type CreateSalesSavedView,
  type CreateSalesSavedViewVariables,
  type DeleteSalesSavedView,
  type DeleteSalesSavedViewVariables,
  type GetSalesSavedViews,
  type UpdateSalesSavedView,
  type UpdateSalesSavedViewOrdering,
  type UpdateSalesSavedViewOrderingToAllAccountManagers,
  type UpdateSalesSavedViewOrderingToAllAccountManagersVariables,
  type UpdateSalesSavedViewOrderingVariables,
  type UpdateSalesSavedViewVariables,
} from "@north-beam/nb-client-side";

import { type SavedViewsContextDescriptor } from "./types";

export const SalesSavedViewsContext = createContext<
  SavedViewsContextDescriptor | undefined
>(undefined);

export function SalesSavedViewsProvider({ children }: { children: ReactNode }) {
  const { data, loading } = useNorthbeamQuery<GetSalesSavedViews>(
    GET_SALES_SAVED_VIEWS,
  );

  const [createSalesViewMutation] = useNorthbeamMutation<
    CreateSalesSavedView,
    CreateSalesSavedViewVariables
  >(CREATE_SALES_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const [updateSalesViewMutation] = useNorthbeamMutation<
    UpdateSalesSavedView,
    UpdateSalesSavedViewVariables
  >(UPDATE_SALES_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const [deleteSalesViewMutation] = useNorthbeamMutation<
    DeleteSalesSavedView,
    DeleteSalesSavedViewVariables
  >(DELETE_SALES_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const [updateSalesViewOrderingMutation] = useNorthbeamMutation<
    UpdateSalesSavedViewOrdering,
    UpdateSalesSavedViewOrderingVariables
  >(UPDATE_SALES_SAVED_VIEW_ORDERING, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const [createHiddenSalesSavedViewTemplateMutation] = useNorthbeamMutation<
    CreateHiddenSalesSavedViewTemplate,
    CreateHiddenSalesSavedViewTemplateVariables
  >(CREATE_HIDDEN_SALES_SAVED_VIEW_TEMPLATE, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const [updateSalesViewOrderingToAllAccountManagersMutation] =
    useNorthbeamMutation<
      UpdateSalesSavedViewOrderingToAllAccountManagers,
      UpdateSalesSavedViewOrderingToAllAccountManagersVariables
    >(UPDATE_SALES_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS, {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
    });

  const createSavedView = useCallback(
    async (name: SavedView["name"], config: SavedView["config"]) =>
      createSalesViewMutation({
        variables: { name, config },
      }),
    [createSalesViewMutation],
  );

  const updateSavedView = useCallback(
    async ({
      id,
      name,
      config,
    }: {
      id: number;
      name?: string;
      config?: SavedView["config"];
    }) => updateSalesViewMutation({ variables: { id, name, config } }),
    [updateSalesViewMutation],
  );

  const deleteSavedView = useCallback(
    async (id: number) => deleteSalesViewMutation({ variables: { id } }),
    [deleteSalesViewMutation],
  );

  const updateSavedViewOrdering = useCallback(
    async (ids: number[], options?: { syncAllAccountManagers?: boolean }) =>
      options?.syncAllAccountManagers
        ? updateSalesViewOrderingToAllAccountManagersMutation({
            variables: { ids },
          })
        : updateSalesViewOrderingMutation({ variables: { ids } }),
    [
      updateSalesViewOrderingMutation,
      updateSalesViewOrderingToAllAccountManagersMutation,
    ],
  );

  const createHiddenSavedViewTemplate = useCallback(
    async (id: number) =>
      createHiddenSalesSavedViewTemplateMutation({ variables: { id } }),
    [createHiddenSalesSavedViewTemplateMutation],
  );

  return (
    <SalesSavedViewsContext.Provider
      value={{
        savedViews: data?.me?.salesSavedViews ?? [],
        loading,
        createSavedView,
        updateSavedView,
        deleteSavedView,
        createHiddenSavedViewTemplate,
        updateSavedViewOrdering,
      }}
    >
      {loading ? (
        <div className="h-[calc(100vh-56px)]">
          <LoadingSlide />
        </div>
      ) : (
        children
      )}
    </SalesSavedViewsContext.Provider>
  );
}
